<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { refDebounced } from '@vueuse/core'
import { useInfiniteQuery as _useInfiniteQuery, keepPreviousData, type InfiniteData } from '@tanstack/vue-query'
import { useAuth } from '@/composables/useAuth.ts'
import { useUserSettings } from '@/composables/useUserSettings.ts'
import { useCreditorSettings } from '@/composables/useCreditorSettings.ts'
import { apiFetch, createApiError } from '@/utils/api.ts'
import NotificationsList from './NotificationsList.vue'
import NotificationSettingsDialog from './NotificationSettingsDialog.vue'
import BTooltipBtn from '@/components/base/BTooltipBtn/BTooltipBtn.vue'
import type { components } from '@/types/_api'
import mdiTuneVerticalVariant from '~icons/mdi/tune-vertical-variant'

type CreditorNotification = components['schemas']['Creditor-Notification']

const { t } = useI18n()
const { isSolicitor } = useAuth()

const { hasSelection } = useCreditorSettings()
const { settings: userSettings } = useUserSettings()

const showNotificationTypesDialog = ref(false)

type TodoFilterName = 'all' | 'decisions' | 'tickets' | 'activities' | 'reversal_handlings'
const selectedTodoFilter = ref<TodoFilterName>('all')

const MAX_RESULTS = 25

// Stricter than the codegen-generated `Dashboard-TodoNotificationsResponse`:
// `_items` is `Record<string, never>[]` there (the Cerberus schema uses
// `allow_unknown: True`) and `_meta` fields are all optional.
type FilterCounts = Record<TodoFilterName, number>
interface TodoNotificationsResponse {
  _items: CreditorNotification[]
  _meta: {
    page: number
    max_results: number
    total: number
  }
  filter_counts: FilterCounts
}

const requestBody = computed(() => {
  const selection = userSettings.value.dashboard?.creditor_selection
  const tagSelection = userSettings.value.dashboard?.creditor_tag_selection ?? []
  return {
    all_creditors_selected: selection?.all_selected === true,
    included_creditors: (selection?.included ?? []).map((item: { creditor: string }) => item.creditor),
    excluded_creditors: (selection?.excluded ?? []).map((item: { creditor: string }) => item.creditor),
    creditor_tags: tagSelection.map((item: { creditor_tag: string }) => item.creditor_tag),
    selected_filter: selectedTodoFilter.value,
    max_results: MAX_RESULTS,
  }
})

// Coalesce rapid filter toggles into one request.
const debouncedRequestBody = refDebounced(requestBody, 200)

// Raw `_useInfiniteQuery` because the codebase wrapper is GET-only.
// `apiFetch.request` is loosened to accept the POST path until the wrapper
// gains POST-query support. POST is used here as a read-only paginated
// aggregation (request body would exceed URL length limits); `useMutation`
// is wrong semantically and lacks the pagination + `keepPreviousData`
// behaviour we depend on.
const requestFn = apiFetch.request as (
  method: string,
  url: string,
  init?: { body?: unknown; signal?: AbortSignal }
) => Promise<{ data?: unknown; error?: unknown; response: Response }>

const notificationsQuery = _useInfiniteQuery<
  TodoNotificationsResponse,
  Error,
  InfiniteData<TodoNotificationsResponse, number>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any,
  number
>({
  queryKey: computed(() => ['post', '/dashboard--todo-notifications', debouncedRequestBody.value] as const),
  queryFn: async (context) => {
    const body = {
      ...debouncedRequestBody.value,
      page: context.pageParam,
    }
    const result = await requestFn('post', '/dashboard--todo-notifications', {
      body,
      signal: context.signal,
    })
    if (result.error != null) {
      throw createApiError(result.error, result.response.status)
    }
    return result.data as TodoNotificationsResponse
  },
  initialPageParam: 1,
  getNextPageParam: (lastPage) => {
    const { page, max_results, total } = lastPage._meta
    return page * max_results < total ? page + 1 : undefined
  },
  enabled: hasSelection,
  placeholderData: keepPreviousData,
})

const pages = computed(() => notificationsQuery.data.value?.pages ?? [])

const notifications = computed<CreditorNotification[]>(() => pages.value.flatMap((page) => page._items))

const todoFilterCounts = computed<FilterCounts>(
  () =>
    pages.value[0]?.filter_counts ?? {
      all: 0,
      decisions: 0,
      tickets: 0,
      activities: 0,
      reversal_handlings: 0,
    }
)

// ADA-4478: the reversal tab is event-driven — it only exists while there is
// something to handle, so creditors without reversal events never see it.
const showReversalHandlingsFilter = computed(() => todoFilterCounts.value.reversal_handlings > 0)

function onReversalHandlingsFilterHidden() {
  if (selectedTodoFilter.value === 'reversal_handlings') {
    selectedTodoFilter.value = 'all'
  }
}
</script>

<template>
  <v-card
    class="d-flex flex-column h-100"
    variant="outlined"
  >
    <section class="pt-6 pb-4 px-6">
      <div class="d-flex justify-space-between align-center mb-2">
        <h2 class="text-body-medium font-weight-bold">{{ t('common.todo') }}</h2>
        <b-tooltip-btn
          v-if="!isSolicitor"
          icon
          size="small"
          variant="text"
          tooltip="adjust_creditor_notifications"
          @click="showNotificationTypesDialog = true"
        >
          <v-icon :icon="mdiTuneVerticalVariant" />
        </b-tooltip-btn>
      </div>
      <v-chip-group
        v-model="selectedTodoFilter"
        mandatory
      >
        <v-chip
          value="all"
          color="brand-primary"
          :variant="selectedTodoFilter === 'all' ? 'flat' : 'outlined'"
          label
          size="default"
          class="filter-chip"
        >
          {{ t('common.all') }} ({{ todoFilterCounts.all }})
        </v-chip>
        <v-chip
          value="decisions"
          color="brand-primary"
          :variant="selectedTodoFilter === 'decisions' ? 'flat' : 'outlined'"
          label
          size="default"
          class="filter-chip"
        >
          {{ t('common.decisions') }} ({{ todoFilterCounts.decisions }})
        </v-chip>
        <v-chip
          value="tickets"
          color="brand-primary"
          :variant="selectedTodoFilter === 'tickets' ? 'flat' : 'outlined'"
          label
          size="default"
          class="filter-chip"
        >
          {{ t('common.tickets') }} ({{ todoFilterCounts.tickets }})
        </v-chip>
        <v-chip
          value="activities"
          color="brand-primary"
          :variant="selectedTodoFilter === 'activities' ? 'flat' : 'outlined'"
          label
          size="default"
          class="filter-chip"
        >
          {{ t('common.activities') }} ({{ todoFilterCounts.activities }})
        </v-chip>
        <v-chip
          v-if="showReversalHandlingsFilter"
          value="reversal_handlings"
          color="brand-primary"
          :variant="selectedTodoFilter === 'reversal_handlings' ? 'flat' : 'outlined'"
          label
          size="default"
          class="filter-chip"
          @vue:unmounted="onReversalHandlingsFilterHidden"
        >
          {{ t('common.reversal_handlings') }} ({{ todoFilterCounts.reversal_handlings }})
        </v-chip>
      </v-chip-group>
    </section>
    <v-card-text class="flex-grow-1 overflow-auto pa-0">
      <notifications-list
        :notifications="notifications"
        :is-loading="notificationsQuery.isLoading.value"
        :is-fetching-next-page="notificationsQuery.isFetchingNextPage.value"
        :has-next-page="notificationsQuery.hasNextPage.value"
        @load-more="() => notificationsQuery.fetchNextPage()"
      />
    </v-card-text>
    <notification-settings-dialog
      v-model="showNotificationTypesDialog"
      :description="t('common.adjust_creditor_notifications_help')"
    />
  </v-card>
</template>
